import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always true, can't be changed
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookie-consent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
      // Apply saved consent to GTM immediately
      updateGTMConsent(parsedConsent);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    
    console.log('🍪 Accept All clicked - Setting consent to:', allConsent);
    setConsent(allConsent);
    saveConsent(allConsent);
    updateGTMConsent(allConsent);
    setShowBanner(false);
    console.log('🍪 Cookie banner hidden, consent saved and GTM updated');
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
    updateGTMConsent(consent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveConsent = (consentData: CookieConsent) => {
    console.log('💾 Saving consent to localStorage:', consentData);
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    // Verify saved data
    const saved = localStorage.getItem('cookie-consent');
    console.log('✅ Consent saved and verified:', JSON.parse(saved || '{}'));
  };

  const updateGTMConsent = (consentData: CookieConsent) => {
    console.log('🔧 Updating GTM consent with:', consentData);
    
    // Send consent update to GTM
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const consentUpdate = {
        analytics_storage: consentData.analytics ? 'granted' : 'denied',
        ad_storage: consentData.marketing ? 'granted' : 'denied',
        functionality_storage: consentData.functional ? 'granted' : 'denied',
        personalization_storage: consentData.marketing ? 'granted' : 'denied',
      };
      
      console.log('📊 GTM consent update:', consentUpdate);
      (window as any).gtag('consent', 'update', consentUpdate);
      
      // Push consent event to dataLayer for GTM triggers
      (window as any).dataLayer = (window as any).dataLayer || [];
      const dataLayerEvent = {
        event: 'cookie_consent_update',
        consent_analytics: consentData.analytics,
        consent_marketing: consentData.marketing,
        consent_functional: consentData.functional
      };
      
      console.log('📡 DataLayer event pushed:', dataLayerEvent);
      (window as any).dataLayer.push(dataLayerEvent);
    } else {
      console.warn('⚠️ GTM not available - consent update skipped');
    }
  };

  const handleConsentChange = (type: keyof CookieConsent, value: boolean) => {
    if (type === 'necessary') return; // Can't change necessary cookies
    setConsent(prev => ({ ...prev, [type]: value }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <Card className="mx-auto max-w-4xl bg-background/95 backdrop-blur-sm border shadow-lg">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">Vi använder cookies</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Vi använder cookies för att förbättra din upplevelse på vår webbplats. 
                  Dessa inkluderar nödvändiga cookies för grundläggande funktionalitet samt 
                  analytiska cookies från Google Tag Manager för att förstå hur du använder vår webbplats.{' '}
                  <Link 
                    to="/cookie-policy" 
                    className="text-primary hover:underline"
                  >
                    Läs mer i vår cookie policy
                  </Link>{' '}
                  eller{' '}
                  <Link 
                    to="/privacy-policy" 
                    className="text-primary hover:underline"
                  >
                    integritetspolicy
                  </Link>.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 min-w-fit">
                <Button
                  variant="outline"
                  onClick={() => setShowSettings(true)}
                  className="gap-2"
                  aria-label="Anpassa cookie-inställningar"
                >
                  <Settings className="h-4 w-4" />
                  Anpassa
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                  aria-label="Tillåt alla cookies"
                >
                  <Check className="h-4 w-4" />
                  Tillåt Alla
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cookie-inställningar</DialogTitle>
            <DialogDescription>
              Hantera dina cookie-preferenser. Nödvändiga cookies kan inte stängas av 
              eftersom de krävs för att webbplatsen ska fungera korrekt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="necessary" className="text-base font-medium">
                    Nödvändiga cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Dessa cookies är nödvändiga för att webbplatsen ska fungera och kan inte stängas av.
                  </p>
                </div>
                <Switch
                  id="necessary"
                  checked={consent.necessary}
                  disabled={true}
                />
              </div>

              <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="analytics" className="text-base font-medium">
                    Analytiska cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Google Tag Manager och liknande verktyg som hjälper oss förstå hur du använder vår webbplats.
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={consent.analytics}
                  onCheckedChange={(checked) => handleConsentChange('analytics', checked)}
                />
              </div>

              <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="marketing" className="text-base font-medium">
                    Marknadsföringscookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Används för att visa relevant innehåll och annonser baserat på dina intressen.
                  </p>
                </div>
                <Switch
                  id="marketing"
                  checked={consent.marketing}
                  onCheckedChange={(checked) => handleConsentChange('marketing', checked)}
                />
              </div>

              <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="functional" className="text-base font-medium">
                    Funktionella cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sparar dina preferenser som språkval och andra inställningar.
                  </p>
                </div>
                <Switch
                  id="functional"
                  checked={consent.functional}
                  onCheckedChange={(checked) => handleConsentChange('functional', checked)}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Avbryt
            </Button>
            <Button onClick={handleSaveSettings}>
              Spara inställningar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner;